import type { FieldNode, ValidationConfig, ValidationContext, ValidationEngineRule } from '../validationEngine/types';

export const getRules = (node: any): ValidationEngineRule[] => {
    return (node?.rules ?? []) as ValidationEngineRule[];
};

export const withoutRequiredRules = (rules: ValidationEngineRule[]): ValidationEngineRule[] => {
    return rules.filter((rule) => rule.type !== 'REQUIRED');
};

export const getRequiredRulesOnly = (rules: ValidationEngineRule[]): ValidationEngineRule[] => {
    return rules.filter((rule) => rule.type === 'REQUIRED');
};

/**
 * Returns a new `ValidationConfig` with all `REQUIRED` rules removed from every field's rule set.
 * Nodes without a `rules` array (e.g. nested groups) are returned unchanged. Use this to reuse an
 * existing flat address-style config while dropping the required-field rules.
 */
export const withoutRequiredRulesFromGroup = (config: ValidationConfig): ValidationConfig => {
    return Object.fromEntries(
        Object.entries(config).map(([field, node]) => {
            const rules = (node as FieldNode)?.rules;
            if (!Array.isArray(rules)) return [field, node];
            return [field, { ...node, rules: withoutRequiredRules(rules) }];
        })
    );
};

export const withoutContractRules = (rules: ValidationEngineRule[]): ValidationEngineRule[] => {
    return rules.filter((rule) => rule.flow !== 'contract');
};

/**
 * Replaces the `conditions.when` predicate on all REQUIRED rules with the provided `whenFn`.
 * Non-REQUIRED rules are left unchanged. Use this to reuse an existing rule set while
 * substituting a different bundle-touched guard (e.g. outside lienholder has no accountNumber).
 */
export const withReplacedWhenCondition = (
    rules: ValidationEngineRule[],
    whenFn: (ctx: ValidationContext, value?: unknown, scope?: unknown) => boolean
): ValidationEngineRule[] => {
    return rules.map((rule) =>
        rule.type === 'REQUIRED' && rule.conditions?.when ? { ...rule, conditions: { ...rule.conditions, when: whenFn } } : rule
    );
};
